import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { db } from './firebase';
import { collection, doc, getDoc, setDoc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import './App.css';

// Components
import Header from './components/Header';
import Chatbot from './components/Chatbot';
import StaffInfo from './components/StaffInfo';

function App() {
  const [staffData, setStaffData] = useState(null);
  const [rsvpData, setRsvpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffUsername, setStaffUsername] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const userIsAuthor = useRef(false);

  // Extract staff username from URL if present
  useEffect(() => {
    const path = window.location.pathname;
    const pathParts = path.split('/');
    if (pathParts.length > 1 && pathParts[1]) {
      setStaffUsername(pathParts[1]);
    }
    setLoading(false);
  }, []);

  // Load staff data if username is present
  useEffect(() => {
    const fetchStaffData = async () => {
      if (!staffUsername) {
        setStaffData(null);
        setRsvpData(null);
        setErrorMessage(null);
        return;
      }

      try {
        setLoading(true);

        // First, try direct username lookup
        const staffRef = doc(db, "staff", staffUsername);
        let staffSnap = await getDoc(staffRef);
        let actualUsername = staffUsername;

        // Check if this is "my" username (the one I've used before)
        const myUsername = localStorage.getItem('myStaffUsername') || '';
        userIsAuthor.current = (myUsername === staffUsername);

        // If not found by username, check if it's an email
        if (!staffSnap.exists() && staffUsername.includes('@')) {
          console.log("Trying email lookup for:", staffUsername);

          // Look up the username via email
          const emailLookupRef = doc(db, "staffEmailLookup", staffUsername.toLowerCase());
          const emailLookupSnap = await getDoc(emailLookupRef);

          if (emailLookupSnap.exists() && emailLookupSnap.data().username) {
            // Found a username by email lookup
            actualUsername = emailLookupSnap.data().username;
            console.log("Found username via email lookup:", actualUsername);

            // Now get the staff data using this username
            const staffByEmailRef = doc(db, "staff", actualUsername);
            staffSnap = await getDoc(staffByEmailRef);

            // Check if this matches my username
            userIsAuthor.current = (myUsername === actualUsername);
          } else {
            // Try querying by email field as fallback
            console.log("Email lookup failed, trying query by email field");
            try {
              const staffByEmailQuery = query(
                collection(db, "staff"),
                where("email", "==", staffUsername)
              );

              const querySnapshot = await getDocs(staffByEmailQuery);
              if (!querySnapshot.empty) {
                staffSnap = querySnapshot.docs[0];
                actualUsername = staffSnap.id;
                console.log("Found via email field query:", actualUsername);

                // Check if this matches my username
                userIsAuthor.current = (myUsername === actualUsername);
              }
            } catch (error) {
              console.error("Error in email query:", error);
            }
          }
        }

        if (staffSnap.exists()) {
          const staffDataValue = staffSnap.data();
          setStaffData(staffDataValue);
          setStaffUsername(actualUsername); // Update to actual username
          setErrorMessage(null);

          // If this is me (author), store my username
          if (userIsAuthor.current || !myUsername) {
            console.log("Setting myStaffUsername to:", actualUsername);
            localStorage.setItem('myStaffUsername', actualUsername);
            userIsAuthor.current = true;
          }

          // Check if staff has already RSVP'd
          const rsvpRef = doc(db, "staffRsvps", actualUsername);
          const rsvpSnap = await getDoc(rsvpRef);

          if (rsvpSnap.exists()) {
            const fullRsvpData = rsvpSnap.data();

            // Check for privacy settings and confirmed status
            const isPrivate = fullRsvpData.privacyEnabled === true;
            const isConfirmed = fullRsvpData.status === "confirmed";

            if (isPrivate && isConfirmed) {
              // This is a private, confirmed RSVP - redact guest details for EVERYONE
              console.log("Redacting private confirmed RSVP data");

              const redactedRsvp = {
                ...fullRsvpData,
                guests: [], // Hide guest details
                guestCount: fullRsvpData.guestCount,
                privacyEnabled: true,
                isRedacted: true,
                staffName: staffDataValue.name,
                staffTitle: staffDataValue.title,
                redactionReason: "This RSVP is private. After confirmation, guest details are permanently hidden."
              };

              setRsvpData(redactedRsvp);
            } else {
              // Either not private or not confirmed yet
              console.log("Showing full RSVP data");
              setRsvpData(fullRsvpData);
            }
          } else {
            setRsvpData(null);
          }
        } else {
          console.log("No such staff member!");
          setStaffData(null);
          setRsvpData(null);

          // Set error message for non-existent staff
          setErrorMessage("We couldn't find your information in our system. Please check your username or email and try again.");
        }
      } catch (error) {
        console.error("Error fetching staff data:", error);
        setErrorMessage("There was an error looking up your information. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchStaffData();
  }, [staffUsername]);

  // Handle staff username/email submission
  const handleStaffLookup = (identifier) => {
    setStaffUsername(identifier);

    if (identifier) {
      // Update URL without refreshing the page
      window.history.pushState(null, '', `/${identifier}`);
    } else {
      // Clear URL for null identifier
      window.history.pushState(null, '', '/');
    }
  };

  // Handle RSVP submission
  const handleRsvpSubmit = async (rsvpData) => {
    try {
      // Always use the current username for saving
      const rsvpRef = doc(db, "staffRsvps", staffUsername);

      if (!rsvpData.createdAt) {
        // New RSVP
        await setDoc(rsvpRef, {
          ...rsvpData,
          createdAt: new Date(),
          updatedAt: new Date(),
          staffUsername: staffUsername,
          // Make sure privacy setting is explicitly set
          privacyEnabled: rsvpData.privacyEnabled === true
        });
      } else {
        // Update existing RSVP
        await updateDoc(rsvpRef, {
          ...rsvpData,
          updatedAt: new Date(),
          // Make sure privacy setting is explicitly set
          privacyEnabled: rsvpData.privacyEnabled === true
        });
      }

      // Update local state
      setRsvpData({
        ...rsvpData,
        updatedAt: new Date(),
        // Make sure privacy setting is explicitly set in local state
        privacyEnabled: rsvpData.privacyEnabled === true,
        isRedacted: false // Since this is being created/updated by the author
      });

      // Set this as "my username"
      localStorage.setItem('myStaffUsername', staffUsername);
      userIsAuthor.current = true;

      return true;
    } catch (error) {
      console.error("Error saving RSVP:", error);
      return false;
    }
  };

  return (
    <Router>
      <div className="app">
        <Header isStaffApp={true} />
        <div className="container">
          <Routes>
            <Route
              path="/"
              element={
                <Chatbot
                  onStaffLookup={handleStaffLookup}
                  onRsvpSubmit={handleRsvpSubmit}
                  staffData={staffData}
                  rsvpData={rsvpData}
                  loading={loading}
                  errorMessage={errorMessage}
                  isStaffApp={true}
                  isAuthor={userIsAuthor.current}
                />
              }
            />
            <Route
              path="/:id"
              element={
                <Chatbot
                  onStaffLookup={handleStaffLookup}
                  onRsvpSubmit={handleRsvpSubmit}
                  staffData={staffData}
                  rsvpData={rsvpData}
                  loading={loading}
                  errorMessage={errorMessage}
                  isStaffApp={true}
                  isAuthor={userIsAuthor.current}
                />
              }
            />
            <Route
              path="/staff-info"
              element={
                staffData ?
                <StaffInfo staffData={staffData} rsvpData={rsvpData} /> :
                <Navigate to="/" />
              }
            />
          </Routes>
        </div>
        <footer className="site-footer">
          <p>Veterans Memorial High School · CTE Senior Awards Ceremony · May 4, 2026</p>
          <p>
            Questions?{' '}
            <a href="mailto:rneal@judsonisd.org">rneal@judsonisd.org</a>
          </p>
        </footer>
      </div>
    </Router>
  );
}

export default App;
